import { alpha, useTheme } from '@mui/material'
import { GridColDef, GridDensity, GridFilterModel, GridSortModel, useGridApiRef } from '@mui/x-data-grid-premium'
import { useEntitiesFromSearch } from 'api/search'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { useEffect, useMemo, useState } from 'react'
import { useUsers } from '../../api/aws/useUsers'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { renderApprovalsColumns } from '../DataGrid/createColumns/renderApprovalsColumns'
import { ApprovalsGridFooter } from './ApprovalsGridFooter'
import { calculateProgress } from './calculateProgress'
import { defaultColumnOrder } from './defaultModelts'
import { gridContainerStyles } from './style'

const defaultSortModel: GridSortModel = [
  { field: 'isApproved', sort: 'asc' },
  { field: 'requestedTime', sort: 'desc' },
  { field: 'approvedTime', sort: 'desc' },
]
const defaultFilterModel: GridFilterModel = {
  items: [],
  quickFilterValues: [],
}
const ApprovalsPage = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const { deals, organizations, isItemsLoading: _isItemsLoading } = useEntitiesFromSearch()
  const { users, isUsersLoading, isUsersError } = useUsers(true, ['approvals', 'users'])
  const filterMenuBorder = `1px solid ${alpha(theme.palette.text.primary, 0.5)}`
  const [isUpdating, setIsUpdating] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  // Load filters from localStorage or use an empty default filter model
  const [filterModel, setFilterModel] = useState(() => {
    const savedFilters = localStorage.getItem('approvalsFilterModel')
    return savedFilters ? JSON.parse(savedFilters) : { items: [], quickFilterValues: [] }
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const savedVisibility = localStorage.getItem('approvalsVisibilityModel')
    return savedVisibility ? JSON.parse(savedVisibility) : {}
  })
  const [sortModel, setSortModel] = useState(() => {
    const savedSortModel = localStorage.getItem('approvalsSortModel')
    return savedSortModel ? JSON.parse(savedSortModel) : defaultSortModel
  })
  // Function to check if the sort model has changed
  const isSortModelChanged = sortModel.some((sort, index) => {
    const initialSort = defaultSortModel[index]
    return sort.field !== initialSort?.field || sort.sort !== initialSort?.sort
  })
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const [density, setDensity] = useState<GridDensity>(() => {
    const saved = localStorage.getItem('approvalsDensityModel') as GridDensity | null
    return saved ?? 'standard'
  })
  const changeDensity = (newDensity: GridDensity) => {
    setDensity(newDensity)
  }
  const [rowCount, setRowCount] = useState(Array.isArray(organizations) ? organizations?.length : 0)
  const apiRef = useGridApiRef()

  useEffect(() => {
    if (filterModel) {
      // Save the filter model to localStorage when it changes
      localStorage.setItem('approvalsFilterModel', JSON.stringify(filterModel))
    }
  }, [filterModel])
  useEffect(() => {
    if (density) {
      // Save the filter model to localStorage when it changes.0
      localStorage.setItem('approvalsDensityModel', density)
    }
  }, [density])
  useEffect(() => {
    const savedFilters = localStorage.getItem('approvalsFilterModel')
    if (savedFilters) {
      setFilterModel(JSON.parse(savedFilters))
    }
  }, []) // Run only on mount
  const processedDeals = useMemo(() => {
    if (!deals) return []
    const filteredDeals = deals.filter(
      deal =>
        deal &&
        deal.isRequested &&
        deal.status !== 'Completed Internally' &&
        Array.isArray(deal.approvers) && // Ensures the deal has approvers
        deal.approvers.length > 0 // Ensures the approvers array is not empty
    )
    return filteredDeals.map(deal => {
      const progressData = calculateProgress(deal.approvers)
      return {
        ...deal,
        rowId: deal?.SK,
        //@ts-ignore
        progress: progressData.individualProgress,
        //@ts-ignore
        progressPercentage: progressData.progressPercentage || 0, // Default to 0 if undefined
      }
    })
  }, [deals])

  const isLoading = isUsersLoading
  const columns = renderApprovalsColumns(processedDeals, users, theme, density) as GridColDef[]

  const handleFilterModelChange = model => {
    if (JSON.stringify(model) !== JSON.stringify(filterModel)) {
      setFilterModel(model)
    }
  }
  const handleDensityChange = newDensity => {
    setDensity(newDensity)
    localStorage.setItem('approvalsDensityModel', newDensity)
  }

  const handleClearSorting = e => {
    e.preventDefault()
    setSortModel([...defaultSortModel]) // Reset the sort model
    localStorage.setItem('approvalsSortModel', JSON.stringify(defaultSortModel)) // Also update localStorage
  }
  const handleSetOpenApprovals = e => {
    e.preventDefault()
    setFilterModel({
      items: [{ field: 'progressPercentage', operator: 'notContains', value: '100' }], // Filter out 100% completed
      quickFilterValues: [],
    })
    setSortModel([...defaultSortModel])
    localStorage.setItem('approvalsSortModel', JSON.stringify(defaultSortModel))
  }

  const handleClearVisibility = e => {
    e.preventDefault()
    const initialVisibilityState = {}
    setColumnVisibilityModel(initialVisibilityState)
    localStorage.setItem('approvalsVisibilityModel', JSON.stringify(initialVisibilityState))
  }
  return (
    <>
      <GlobalDataGrid
        columns={columns}
        rows={processedDeals}
        apiRef={apiRef}
        context={'approvals'}
        defaultFilterModel={defaultFilterModel}
        defaultVisibilityModel={{}}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnOrder={defaultColumnOrder}
        onChangeDensity={changeDensity}
        pinnedColumns={{ left: ['__check__', 'name'], right: [] }}
        // onRowClick={handleRowClick}
        toolbarComponent={GlobalGridToolbar}
        isLoading={isLoading || isUpdating}
        FooterComponent={ApprovalsGridFooter}
        treeData={false}
        gridContainerSx={{
          position: 'relative',
          ...gridContainerStyles,
          pt: pinned ? '60px' : 0.8,
          transition: 'all 0.3s ease', // or .5s, etc.
          height: '98%',
        }}
      />
    </>
  )
}

export default ApprovalsPage
